'use client';

import { useEffect, useRef, useState } from 'react';

import { useInView } from 'motion/react';

interface CountingNumberProps {
  value: number;
  from?: number;
  className?: string;
  prefix?: string;
  suffix?: string;
  decimalPlaces?: number;
}

const START_RATIO = 0.6;
const DURATION_MS = 900;

// Ease out cubic for smooth deceleration
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export function CountingNumber({
  value,
  from,
  className,
  prefix = '',
  suffix = '',
  decimalPlaces = 0,
}: CountingNumberProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.8 });
  const startValue = from ?? value * START_RATIO;
  const [displayValue, setDisplayValue] = useState(startValue);

  useEffect(() => {
    if (!isInView) return;

    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const progress = Math.min((currentTime - startTime) / DURATION_MS, 1);
      const current = startValue + (value - startValue) * easeOutCubic(progress);
      setDisplayValue(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isInView, value, startValue]);

  const format = (v: number) =>
    decimalPlaces > 0 ? v.toFixed(decimalPlaces) : Math.floor(v).toString();
  const formattedValue = format(displayValue);
  const spacer =
    format(startValue).length >= format(value).length ? format(startValue) : format(value);

  return (
    <span ref={ref} className={`inline-grid tabular-nums ${className ?? ''}`}>
      <span className="invisible col-start-1 row-start-1" aria-hidden="true">
        {prefix}
        {spacer}
        {suffix}
      </span>
      <span className="col-start-1 row-start-1">
        {prefix}
        {formattedValue}
        {suffix}
      </span>
    </span>
  );
}
